import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { assetUrl } from "../api";
import { label, money } from "../format";
import Icon from "./icons";

export function Page({ title, text, actions, children }) {
  return (
    <section className="page">
      <header className="page-head">
        <div>
          <h1>{title}</h1>
          {text ? <p>{text}</p> : null}
        </div>
        {actions || null}
      </header>
      {children}
    </section>
  );
}

export function Alerts({ error, notice }) {
  return (
    <>
      {error ? <div className="alert">{error}</div> : null}
      {notice ? <div className="alert ok">{notice}</div> : null}
    </>
  );
}

export function Loading() {
  return <p className="loading">Loading...</p>;
}

export function Badge({ value }) {
  if (!value) return <span className="muted">—</span>;
  return <span className={"badge " + value}>{label(value)}</span>;
}

export function Tabs({ value, options, onChange }) {
  return (
    <div className="tabs">
      {options.map((option) => (
        <button
          key={option.label}
          type="button"
          className={option.value === value ? "tab active" : "tab"}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

export function Pager({ page, limit, total, onPage }) {
  const count = Number(total) || 0;
  const pages = Math.max(1, Math.ceil(count / limit));
  const current = Math.min(Math.max(page, 1), pages);
  return (
    <div className="pager">
      <span>{count} records</span>
      <div>
        <button type="button" className="btn ghost small" disabled={current <= 1} onClick={() => onPage(current - 1)}>
          Previous
        </button>
        <span>Page {current} of {pages}</span>
        <button type="button" className="btn ghost small" disabled={current >= pages} onClick={() => onPage(current + 1)}>
          Next
        </button>
      </div>
    </div>
  );
}

export function Field({ label: caption, error, hint, children }) {
  return (
    <label className="field">
      <span>{caption}</span>
      {children}
      {hint ? <small className="hint">{hint}</small> : null}
      {error ? <small className="field-error">{error}</small> : null}
    </label>
  );
}

export function EmptyRow({ cols, text }) {
  return (
    <tr>
      <td className="empty" colSpan={cols}>
        <div className="empty-state">
          <span className="empty-icon"><Icon name="inbox" /></span>
          <p>{text || "No records"}</p>
        </div>
      </td>
    </tr>
  );
}

export function SignedAmount({ value, direction }) {
  const out = direction === "debit";
  return <td className={out ? "num out" : "num in"}>{(out ? "−" : "+") + money(value)}</td>;
}

export function Avatar({ src, name }) {
  const url = assetUrl(src);
  const letter = String(name || "?").trim().charAt(0).toUpperCase() || "?";
  if (!url) return <span className="avatar letter">{letter}</span>;
  return <img className="avatar" src={url} alt="" />;
}

export function Photo({ src, alt, className }) {
  const url = assetUrl(src);
  if (!url) return <span className="muted">No image</span>;
  return <img className={className || "photo"} src={url} alt={alt || ""} />;
}

export function UserLink({ user, userId }) {
  const id = (user && user.id) || userId;
  if (!user && !id) return <span className="muted">—</span>;
  if (!user || !user.name) {
    return id ? <Link to={"/admin/members/" + id}>Member</Link> : <span className="muted">—</span>;
  }
  return (
    <Link to={"/admin/members/" + user.id} className="user-link">
      <strong>{user.name}</strong>
      <span>{user.email}</span>
      {user.referralCode ? <span className="code">{user.referralCode}</span> : null}
    </Link>
  );
}

export function Facts({ items }) {
  return (
    <dl className="facts">
      {items.map((item) => (
        <div key={item.label} className={item.wide ? "wide" : ""}>
          <dt>{item.label}</dt>
          <dd>{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}

export function Modal({ title, wide, onClose, children }) {
  useEffect(() => {
    function onKey(event) {
      if (event.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previous;
    };
  }, [onClose]);

  return createPortal(
    <div className="modal-back" onMouseDown={onClose}>
      <div
        className={wide ? "modal wide" : "modal"}
        role="dialog"
        aria-modal="true"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="modal-head">
          <h2>{title}</h2>
          <button type="button" className="btn ghost small" onClick={onClose}>Close</button>
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>,
    document.body
  );
}

export function ConfirmDialog({ title, text, error, busy, confirmLabel, danger, onClose, onConfirm }) {
  return (
    <Modal title={title} onClose={onClose}>
      <p className="dialog-text">{text}</p>
      {error ? <div className="alert">{error}</div> : null}
      <div className="form-actions">
        <button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
        <button type="button" className={danger ? "btn danger" : "btn"} disabled={busy} onClick={onConfirm}>
          {busy ? "Working..." : confirmLabel || "Confirm"}
        </button>
      </div>
    </Modal>
  );
}

export function ReasonDialog({ title, label: caption, hint, error, busy, min = 3, max, onClose, onSubmit }) {
  const [value, setValue] = useState("");
  const [localError, setLocalError] = useState("");

  function submit(event) {
    event.preventDefault();
    const text = value.trim();
    if (text.length < min || (max && text.length > max)) {
      setLocalError(caption + " must be " + min + (max ? " to " + max : " or more") + " characters");
      return;
    }
    setLocalError("");
    onSubmit(text);
  }

  return (
    <Modal title={title} onClose={onClose}>
      <form onSubmit={submit}>
        <Field label={caption} hint={hint} error={localError || error}>
          <textarea rows={4} maxLength={max || undefined} value={value} onChange={(event) => setValue(event.target.value)} />
        </Field>
        <div className="form-actions">
          <button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="btn danger" disabled={busy}>{busy ? "Working..." : "Reject"}</button>
        </div>
      </form>
    </Modal>
  );
}
